<template>
  <header
    id="header"
    class="anim-position-width bg-primary flex items-center h-header border-b md:pl-15 text-app-color relative"
    :class="[
      showHeaderClasses,
      showInstructions ? 'border-onboarding-backdrop' : isDarkMode ? 'border-project' : 'border-grey-575 ',
      {'fixed z-40 px-10': isMobile},
      showSidebar && isMobile ? 'w-full md:w-calc-sidebar' : 'w-full'
    ]"
  >
    <div v-if="showInstructions" class="absolute w-full h-full z-10 bg-onboarding-backdrop left-0" />

    <div class="flex w-full h-full items-center justify-between anim-opacity">
      <div class="flex items-center h-full">
        <ButtonModule
          v-if="showSidebarToggle"
          class="text-18 px-10 py-6 mr-10 text-grey-250 hover:text-app-color"
          prefix="icon-menu text-22"
          @click="toggleSidebar"
        />

        <SpaceDropdown v-if="!isBooksPage" />
      </div>

      <div v-if="!isBooksPage" class="h-full flex items-center">
        <MobileMenu v-if="isMobile || isFocusMode" class="md:mr-20 my-auto" @goToRoute="goToRoute" />

        <div v-else class="flex items-center h-full">
          <div class="flex items-center h-full">
            <ButtonModule
              text="Pages"
              prefix="icon-projects"
              class="relative h-full py-0 border-b-2 outline-none border-opacity-0 px-5 mx-7 hover:border-opacity-50 text-15"
              :class="[{'border-opacity-100 hover:border-opacity-100 font-medium': isBookPage}, isDarkMode ? 'border-white': 'border-dark-600']"
              :has-hover="false"
              @click="goToProjects"
            />
            <ButtonModule
              text="Tasks"
              prefix="icon-task_board"
              class="relative h-full py-0 border-b-2 outline-none border-opacity-0 px-5 mx-7 hover:border-opacity-50 text-15"
              :class="[{'border-opacity-100 hover:border-opacity-100 font-medium': isTaskBoard}, isDarkMode ? 'border-white': 'border-dark-600']"
              :has-hover="false"
              @click="goToTaskBoard"
            />
            <ButtonModule
              v-if="isTeamAccount"
              text="Discussions"
              prefix="icon-discussion"
              class="relative h-full py-0 border-b-2 outline-none border-opacity-0 px-5 mx-7 hover:border-opacity-50 text-15"
              :class="[{'border-opacity-100 hover:border-opacity-100 font-medium': isDiscussion}, isDarkMode ? 'border-white': 'border-dark-600']"
              :has-hover="false"
              @click="goToRoute('Discussions')"
            />
            <div class="relative flex h-full" @click="goToWeekPlanner">
              <div v-if="isFreePlan" class="absolute top-5 z-10 right-0 py-1 px-5 font-semibold bg-brand text-8 rounded-4 cursor-pointer text-white">PRO</div>
              <ButtonModule
                text="Week planner"
                prefix="icon-week_planner text-19"
                class="h-full border-b-2 outline-none border-opacity-0 px-5 mx-7 hover:border-opacity-50 text-15"
                :class="[{'border-opacity-100 hover:border-opacity-100 font-medium': isWeekPlanner}, isDarkMode ? 'border-white': 'border-dark-600']"
                :has-hover="false"
              />
            </div>
            <div class="relative h-full">
              <span v-if="hasUnreadMessages && !chatMutedAt" class="w-6 h-6 rounded-full absolute top-3 z-100 left-24 bg-yellow-500" />
              <ButtonModule
                v-if="isTeamAccount"
                text="Chat"
                prefix="icon-chat1"
                class="h-full border-b-2 outline-none border-opacity-0 px-5 mx-7 hover:border-opacity-50 text-15"
                :class="[{'border-opacity-100 hover:border-opacity-100 font-medium': isChatPage}, isDarkMode ? 'border-white': 'border-dark-600']"
                :has-hover="false"
                @click="goToRoute('Chat')"
              />
            </div>
            <ButtonModule
              text="Files"
              prefix="icon-file-manager"
              class="relative h-full py-0 border-b-2 outline-none border-opacity-0 px-5 mx-7 md:mr-30 hover:border-opacity-50 text-15"
              :class="[{'border-opacity-100 hover:border-opacity-100 font-medium': isFileManager}, isDarkMode ? 'border-white': 'border-dark-600']"
              :has-hover="false"
              @click="goToFileManager"
            />
          </div>
        </div>
      </div>
    </div>
    <WeekPlannerPopup ref="weekPlannerPopupRef" />
  </header>
</template>

<script>
import ButtonModule from '@/components/shared/form-controls/ButtonModule'
import SpaceDropdown from '@/components/header/SpaceDropdown'

import { defineComponent, computed, ref, onMounted, watch } from 'vue'
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import localStorageService from '@/core/local-storage'
import { preventBodyScroll } from '@/core/helper-functions'
import WeekPlannerPopup from '@/components/week-planner/WeekPlannerPopup'
import MobileMenu from '@/components/header/MobileMenu'

export default defineComponent({
  components: {
    ButtonModule,
    SpaceDropdown,
    WeekPlannerPopup,
    MobileMenu
  },

  emits: ['toggleSidebar'],

  setup (props, { emit }) {
    const { state, getters, commit } = useStore()
    const route = useRoute()

    const settings = computed(() => state.settings.settings)
    const showSidebar = computed(() => state.settings.showSidebar)
    const showHeader = computed(() => state.settings.showHeader)
    const currentProject = computed(() => state.project.currentProject)
    const isProjectInstructionsAccepted = computed(() => state.profile.user?.profile?.showProjectOnboarding)

    const showInstructions = computed(() => {
      return !getters.isProjectInstructionAccepted && state.profile.activeStep > 1
    })

    const isTeamAccount = computed(() => getters.isTeamAccount)
    const isMobile = computed(() => getters.isMobile)
    const isDarkMode = computed(() => getters.isDarkMode)
    const isMagenta = computed(() => getters.isMagenta)
    const isFreePlan = computed(() => getters.isFreePlan)
    const isFocusMode = computed(() => state.settings.isFocusMode)

    const isHeadquarters = computed(() => route.name === 'Headquarters')
    const isBooksPage = computed(() => route.name === 'Books' || route.name === 'TeamBooks')
    const isBookPage = computed(() => route.name === 'Book' || route.name === 'TeamBook' || route.name === 'Headquarters')
    const isTaskBoard = computed(() => route.name === 'TaskBoard' || route.name === 'TaskBoardTeam')
    const isWeekPlanner = computed(() => route.name === 'WeekPlanner' || route.name === 'WeekPlannerTeam')
    const isFileManager = computed(() => route.name === 'FileManager' || route.name === 'FileManagerTeam')
    const isChatPage = computed(() => route.name === 'Chat')
    const isDiscussion = computed(() => route.name === 'Discussions' || route.name === 'Discussion')
    const isToolPage = computed(() => ['TaskBoard', 'TaskBoardTeam', 'WeekPlanner', 'WeekPlannerTeam', 'Discussions', 'Discussion', 'Chat', 'FileManager', 'FileManagerTeam'].includes(route.name))

    const showSidebarToggle = computed(() => {
      return (((isBookPage.value || isToolPage.value) && !showSidebar.value) || isMobile.value)
    })

    const toggleButtonClasses = computed(() => {
      const prefix = showSidebar.value ? '' : 'hover:'
      return `${isDarkMode.value.value ? `${prefix}bg-dark-500 ${prefix}opacity-100` : `${prefix}bg-menu-hover ${prefix}opacity-100`}`
    })

    const showHeaderClasses = computed(() => {
      if (isMobile.value) {
        return showHeader.value ? 'top-0' : '-top-62'
      }
    })

    function toggleSidebar () {
      emit('toggleSidebar')
    }

    const router = useRouter()

    const weekPlannerPopupRef = ref(null)

    const book = computed(() => state.book.book)
    const books = computed(() => state.book.books)
    const team = computed(() => state.team.team)
    const headquarters = computed(() => state.team.headquarters)

    const chatMutedAt = computed(() => state.chat.chatMutedAt)
    const hasUnreadMessages = computed(() => state.chat.hasUnreadMessages)

    const showChat = ref(false) // TODO: move to store and use for prevent hot key on discussion page

    watch(() => route.query.openChat, value => {
      showChat.value = !!value
    })

    onMounted(() => {
      showChat.value = !!route.query.openChat
    })

    function goToProjects () {
      if (isBookPage.value) return

      const name = isTeamAccount.value ? 'TeamBook' : 'Book'

      goToRoute(name)
    }

    function goToWeekPlanner () {
      if (isFreePlan.value) {
        weekPlannerPopupRef.value.open()
        return
      }

      const name = isTeamAccount.value ? 'WeekPlannerTeam' : 'WeekPlanner'

      goToRoute(name)
    }

    function goToTaskBoard () {
      const name = isTeamAccount.value ? 'TaskBoardTeam' : 'TaskBoard'

      goToRoute(name)
    }

    function goToFileManager () {
      const name = isTeamAccount.value ? 'FileManagerTeam' : 'FileManager'

      goToRoute(name)
    }

    function goToRoute (name) {
      const routesWithSpaces = ['Discussions', 'WeekPlannerTeam', 'WeekPlanner', 'FileManagerTeam', 'FileManager']
      const hasSpaces = routesWithSpaces.includes(name)

      const bookId = book.value?.id || route.params.id
      let bookIds = book.value?.id || books.value.filter(b => !b.isSection).map(b => b.id).join(',')
      const params = { id: bookId }

      if (isTeamAccount.value) {
        bookIds += book.value?.id ? '' : `,${headquarters.value.id}`
        params.team = team.value.link
      }

      const query = hasSpaces ? { spaces: bookIds } : null

      router.push({ name, params, query })
    }

    function backToBooks () {
      localStorageService.removeItem('lastOpenedProject')

      isTeamAccount.value
        ? router.push({ name: 'TeamBooks', params: { team: team.value.link } })
        : router.push({ name: 'Books' })

      if (isMobile.value) {
        commit('updateSidebar', false)
      }
    }

    function openChat () {
      preventBodyScroll(true)
      showChat.value = true
      // dispatch('updateMessageStatus', { chatId: book.value.chat.id })
    }

    function closeChat () {
      preventBodyScroll(false)
      showChat.value = false

      const query = Object.assign({}, route.query)
      delete query.openChat
      router.replace({ query })
    }

    return {
      // ref
      weekPlannerPopupRef,
      // data
      // computed
      showChat,
      isBookPage,
      isFreePlan,
      isDarkMode,
      isChatPage,
      isBooksPage,
      isTaskBoard,
      chatMutedAt,
      isDiscussion,
      isTeamAccount,
      isWeekPlanner,
      isFileManager,
      isHeadquarters,
      hasUnreadMessages,
      settings,
      showSidebar,
      showHeader,
      isFocusMode,
      currentProject,
      isProjectInstructionsAccepted,
      isMobile,
      isMagenta,
      isToolPage,
      showSidebarToggle,
      showInstructions,
      showHeaderClasses,
      toggleButtonClasses,
      // function
      toggleSidebar,
      openChat,
      goToRoute,
      closeChat,
      backToBooks,
      goToProjects,
      goToTaskBoard,
      goToWeekPlanner,
      goToFileManager
    }
  }
})
</script>
